import { useCallback, useMemo, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Form as FinalForm, Field } from 'react-final-form';
import * as yup from 'yup';

import Form from '@guestyci/foundation/Form';
import TextField from '@guestyci/foundation/TextField';
import { Row, Col } from '@guestyci/foundation/Layout';
import createStyles from '@guestyci/foundation/createStyles';
import defaultTheme from '@guestyci/foundation/theme/defaultTheme';
import { FormProvider } from '@guestyci/foundation/enums';

import track from 'utils/dio/login-page';
import InputField from 'app/shareable/inputs/InputField';
import useAuthorizationModel from 'app/hooks/authorization/useAuthorizationModel';
import utils from 'utils';
import constant from 'constant';
import FormButton from 'app/shareable/FormButton';
import CheckboxUI from 'app/shareable/CheckboxUI';
import ErrorMessage from 'app/shareable/ErrorMessage';
import navigationsRoute from 'app/routes/navigationsRoute';
import GoogleSignInButton from './GoogleAuthButton';
import { useAuthorizationContext } from './AuthorizationContainer';
import config from 'config';

const useStyles = createStyles((theme: typeof defaultTheme) => ({
  link: {
    color: `${theme.palette.text.dark}!important`,
  },
}));

const validation = yup.object().shape({
  email: yup
    .string()
    .email('Invalid email address')
    .required('This field is required'),
  password: yup
    .string()
    .label('Password')
    .min(6)
    .required('This field is required'),
});

const validate = utils.validateFormValues(validation);

function LoginForm() {
  const {
    login,
    loginSSO,
    isLoading,
    responseErrorMessage,
    ssoZendesk,
    isZendeskAuthorization,
  } = useAuthorizationModel();
  const { actions } = useAuthorizationContext();
  const classes = useStyles();
  const { search, hash } = useLocation();
  const queryParams = useRef(new URLSearchParams(search));
  const returnTo = queryParams.current.get('return_to');
  const formRef = useRef<HTMLFormElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const defaultEmail = useMemo(() => {
    return (
      queryParams.current.get('email') ||
      hash.replace(/#\/unauthenticated\/login\/?/, '')
    );
  }, [search, hash]);

  const handleGoogleSSO = useCallback(
    (google) => {
      loginSSO(google).then(({ link }) => {
        window.location.replace(link);
      });
    },
    [loginSSO]
  );

  const onSubmit = useCallback(
    (values) => {
      const { name, version } = utils.getBrowserVersion();
      const generatedDeviceUUID = utils.generateUUID();
      const { email, password, rememberMe } = values;

      // handle dio event after click `login`
      track.loginPageClickLogin();

      login({
        email,
        password,
        osVersion: version,
        manufacturer: name,
        requestDate: utils.getTimestamp().toString(),
        deviceUUID: generatedDeviceUUID,
        platform: 'browser--macintel',
        deviceToken: 'undefined',
        appVersion: constant.IONIC_APP_VERSION,
        model: 'undefined',
      }).then((response) => {
        utils.disableAutologinSession();
        utils.setLocalStorage({
          emailAddress: email,
          rememberMe,
          sessionToken: response.sessionToken,
          authenticatedData: response,
          uuid: generatedDeviceUUID,
          dateTimeFormat: response.dateTimeFormat,
        });

        if (!rememberMe) {
          utils.setUserSession();
        }

        if (isZendeskAuthorization) {
          ssoZendesk().then((jwt) => {
            if (inputRef?.current) {
              inputRef.current.value = jwt;
            }
            formRef?.current?.submit();
          }
          );
        } else {
          actions.login();
        }
      });
    },
    [actions, login]
  );

  return (
    <>
      <FinalForm
        initialValues={{ rememberMe: true, email: defaultEmail }}
        onSubmit={onSubmit}
        validate={validate}
        render={({ handleSubmit }) => (
          <Form
            data-qa="login-form"
            onSubmit={handleSubmit}
            noValidate
            provider={FormProvider.Final}
            fieldInstance={Field}
          >
            <Col spacing={5}>
              <InputField
                disabled={isLoading}
                label="Email"
                name="email"
                type="email"
                placeholder="Email"
              />
              <InputField
                disabled={isLoading}
                label="Password"
                name="password"
                type="password"
                placeholder="Password"
              />
              <Row className="my-2 font-size-lg" align="center">
                <Col fullWidth>
                  <CheckboxUI name="rememberMe" label="Remember me" />
                </Col>
                <Col fullWidth align="end">
                  <Link
                    className={classes.link}
                    to={navigationsRoute.FORGOT_PASSWORD}
                  >
                    Forgot password?
                  </Link>
                </Col>
              </Row>

              <Row className="mt-11">
                <FormButton disabled={isLoading} isLoading={isLoading}>
                  <TextField className="font-size-lg text-uppercase">
                    Log In
                  </TextField>
                </FormButton>
              </Row>

              <ErrorMessage icon message={responseErrorMessage} />

              <a
                href="https://help.guestyforhosts.com/hc/en-gb/articles/9350499121053-Troubleshooting-Login-Issues"
                rel="noreferrer"
                target="_blank"
              >
                <TextField
                  color="dark"
                  align="center"
                  className="underline font-size-xl font-weight-bold"
                >
                  Need Help ?
                </TextField>
              </a>

              <GoogleSignInButton onSignup={handleGoogleSSO} />
            </Col>
          </Form>
        )}
      />
        <form
        ref={formRef}
        action={`https://${config.zendeskSubDomain}.zendesk.com/access/jwt${returnTo ? '?return_to=' + returnTo : '/' }`}
        method="post"
      >
        <input ref={inputRef} type="hidden" name="jwt"></input>
      </form>
    </>
  );
}

export default LoginForm;
